import {
  type ApiRequest,
  deleteAPI,
  properCallApi,
  type ApiResponseSingle,
  callApi,
} from '@/api/tenantClient';

import { type DiscountCode } from './discountCode';
import { type RegistrationStep } from './registration';
import { type TempRegistration } from './tempRegistration';

export const groupRegistrationQueryKey = 'groupRegistration.registration';
export type GroupRegistrationSteps = RegistrationStep[];

export enum GroupRegistrationStatus {
  PENDING = 'pending',
  AWAITING_PAYMENT = 'awaiting_payment',
  CANCELLED = 'cancelled',
  COMPLETED = 'completed',
}

export interface RegistrationItem {
  id: number
  email: string
  name: string
  registrationType: string
  status: string
}

export interface GroupRegistration {
  id: number
  email: string
  firstName?: string
  lastName?: string
  attendeeCategoryId: number
  attendeeCategory: {
    id: number
    name: string
    maximumAttendees: number | null
    minimumAttendees: number | null
  }
  registrations: RegistrationItem[]
  discountId?: number
  tmpIdPhotocopy?: string
  idPhotocopy?: string
  idInfo?: string
  termsAndConditionsAccepted: boolean
  privacyPolicyAccepted: boolean
  eventId: string
  orderHash?: string
  status: string
  event: {
    id: string
    name: string
  }
  createdById: number
  createdAt: string
  groupRegistrationSteps: GroupRegistrationSteps
  discount: DiscountCode
  tempRegistrations: TempRegistration[]
}

export interface GroupRegistrationQueryRequest {
  email: string
  eventId: string
}

export interface ReassignRegistration {
  email: string
  registrationId: number
  groupRegistrationId: number
}

export function loadGroupRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: GroupRegistrationQueryRequest
  ): Promise<ApiResponseSingle<GroupRegistration>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/group_registrations/load_group_registration';
    request.payload = {
      email: params.email,
      eventId: params.eventId
    };
    return await properCallApi<ApiResponseSingle<GroupRegistration>>(request);
  };
}

export interface GroupRegistrationSaveRequest extends Omit<GroupRegistration, 'id' | 'expiry' | 'registrationSteps'> {
  eventId: string
}

export interface GroupRegistrationDeleteRequest {
  id: string
}

export function deleteGroupRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (params: GroupRegistrationDeleteRequest) => {
    request.method = 'DELETE';
    request.endpoint.path = '/api/v1/group_registrations/' + params.id;
    await deleteAPI(request);
  };
}

export interface GroupRegistrationUpdateRequest {
  groupRegistration: {
    id: number
    attendeeCategoryId?: number
    discountId?: number
    tmpIdPhotocopy?: string
    idInfo?: string
    termsAndConditionsAccepted: boolean
    privacyPolicyAccepted: boolean
  }
}

export function updateGroupRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (
    params: GroupRegistrationUpdateRequest
  ): Promise<ApiResponseSingle<GroupRegistration>> => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/group_registrations/update';
    request.payload = params;

    return await properCallApi<ApiResponseSingle<GroupRegistration>>(request);
  };
}

export function reassignRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (params: ReassignRegistration) => {
    request.method = 'POST';
    request.endpoint.path = '/api/v1/group_registrations/reassign_registration';
    request.payload = params;
    return await callApi(request);
  };
}

export interface GroupRegistrationValidAttendeeRequest {
  id: number
}

export function validAttendeeGroupRegistration(createTenantRequest: () => ApiRequest) {
  const request = createTenantRequest();
  return async (params: GroupRegistrationValidAttendeeRequest) => {
    request.method = 'GET';
    request.endpoint.path = `/api/v1/group_registrations/valid_attendees?id=${params.id}`;
    return await properCallApi<ApiResponseSingle<GroupRegistration>>(request);
  };
}
